import React, { useState } from 'react';
import { leadsAPI, whatsappAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card, StageBadge, PriorityBadge } from '../../../components/ui';
import { LEAD_STAGES, PRIORITIES, LEAD_SOURCES, INDIA_STATES, CUSTOMER_TYPES, PROGRAM_FEE } from '../../../utils/constants';
import TypeSpecificFields from '../../../components/TypeSpecificFields';
import toast from 'react-hot-toast';
import WhatsAppModal from '../../../components/WhatsAppModal';

const FIELD_ROW = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', width: 160, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, color: '#334155' }}>{value || <span style={{ color: '#cbd5e1' }}>—</span>}</span>
  </div>
);

export default function OverviewTab({ lead, onUpdate, teamMembers }) {
  const [waModal, setWaModal] = useState(false);
  const { user, isAdmin, isAdminOrMD, canViewAllLeads, canAssignLeads } = useAuth();
  const [editing, setEditing] = useState(false);

  // RCM can view all regional leads but edit only ones they have a direct relation to
  const isRcm = user?.sub_role === 'RCM';
  const uid = String(user?._id || '');
  const hasDirectRelation = !isRcm || (
    String(lead.assigned_to?._id || lead.assigned_to || '') === uid ||
    String(lead.created_by?._id  || lead.created_by  || '') === uid ||
    (lead.collaborators || []).some(c => String(c._id || c) === uid)
  );
  const canEdit = hasDirectRelation;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    stage: lead.stage,
    priority: lead.priority,
    source: lead.source || '',
    region: lead.region || '',
    contact_name: lead.contact_name || '',
    contact_email: lead.contact_email || '',
    contact_phone: lead.contact_phone || '',
    assigned_to: lead.assigned_to?._id || '',
    notes: lead.notes || '',
    deal_value: lead.deal_value || '',
    win_loss_reason: lead.win_loss_reason || '',
  });

  // Collaborators state
  const [collabAdding, setCollabAdding] = useState(false);
  const [collabSelected, setCollabSelected] = useState('');
  const [collabSaving, setCollabSaving] = useState(false);

  // type_specific_data is edited separately so the TypeSpecificFields component
  // can remain a controlled component with its own (key, value) setter shape.
  const [typeData, setTypeData] = useState(lead.type_specific_data || {});
  const setT = (key, val) => setTypeData(d => ({ ...d, [key]: val }));

  const inpStyle = { width: '100%', padding: '7px 11px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
  const selStyle = { ...inpStyle, cursor: 'pointer' };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, type_specific_data: typeData };
      if (!payload.assigned_to) delete payload.assigned_to;
      const res = await leadsAPI.update(lead._id, payload);
      onUpdate(res.data?.data);
      setEditing(false);
      toast.success('Lead updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setTypeData(lead.type_specific_data || {});
    setEditing(true);
  };

  const cancelEditing = () => {
    setForm({
      stage: lead.stage,
      priority: lead.priority,
      source: lead.source || '',
      region: lead.region || '',
      contact_name: lead.contact_name || '',
      contact_email: lead.contact_email || '',
      contact_phone: lead.contact_phone || '',
      assigned_to: lead.assigned_to?._id || '',
      notes: lead.notes || '',
      deal_value: lead.deal_value || '',
      win_loss_reason: lead.win_loss_reason || '',
    });
    setTypeData(lead.type_specific_data || {});
    setEditing(false);
  };

  const addCollaborator = async () => {
    if (!collabSelected) return;
    setCollabSaving(true);
    try {
      const res = await leadsAPI.update(lead._id, { add_collaborators: [collabSelected] });
      onUpdate(res.data?.data);
      setCollabAdding(false);
      setCollabSelected('');
      toast.success('Collaborator added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add collaborator');
    } finally {
      setCollabSaving(false);
    }
  };

  const removeCollaborator = async (collaboratorId) => {
    setCollabSaving(true);
    try {
      const res = await leadsAPI.update(lead._id, { remove_collaborators: [collaboratorId] });
      onUpdate(res.data?.data);
      toast.success('Collaborator removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove collaborator');
    } finally {
      setCollabSaving(false);
    }
  };

  const needsReason = form.stage === 'Partnership Closed' || form.stage === 'Lost';

  const tsd = lead.type_specific_data || {};
  const collaborators = lead.collaborators || [];

  // Team members eligible to be added as collaborators:
  // - not already collaborating
  // - not Intern or Support Member (collaborators check is ignored for these roles)
  const INELIGIBLE_ROLES = ['Intern', 'Support Member'];
  const availableToAdd = teamMembers.filter(
    m => !collaborators.some(c => c._id === m._id) &&
         !INELIGIBLE_ROLES.includes(m.sub_role)
  );

  return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      {/* Left column */}
      <div>
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B' }}>Lead Details</h3>
            {!editing && canEdit && <Button size="sm" variant="outline" onClick={startEditing}>Edit</Button>}
            {!editing && !canEdit && (
              <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', padding: '4px 10px', borderRadius: 6 }}>
                View Only
              </span>
            )}
            {editing && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" variant="ghost" onClick={cancelEditing}>Cancel</Button>
                <Button size="sm" variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            )}
          </div>

          {editing ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>STAGE</label>
                <select value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))} style={selStyle}>
                  {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>PRIORITY</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={selStyle}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>SOURCE</label>
                <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} style={selStyle}>
                  <option value="">—</option>
                  {LEAD_SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>REGION</label>
                <select value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} style={selStyle}>
                  <option value="">—</option>
                  {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {canAssignLeads && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>ASSIGNED TO</label>
                  <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} style={selStyle}>
                    <option value="">Unassigned</option>
                    {teamMembers.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>DEAL VALUE (₹)</label>
                <input type="number" value={form.deal_value} onChange={e => setForm(f => ({ ...f, deal_value: e.target.value }))} style={inpStyle} />
              </div>
              {needsReason && (
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>WIN/LOSS REASON *</label>
                  <input value={form.win_loss_reason} onChange={e => setForm(f => ({ ...f, win_loss_reason: e.target.value }))} style={inpStyle} placeholder="Required for this stage" />
                </div>
              )}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>NOTES</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inpStyle, minHeight: 70, resize: 'vertical' }} />
              </div>
            </div>
          ) : (
            <>
              <FIELD_ROW label="Lead ID" value={<span style={{ fontWeight: 700, color: '#1B2A6B' }}>{lead.lead_id}</span>} />
              <FIELD_ROW label="Stage" value={<StageBadge stage={lead.stage} />} />
              <FIELD_ROW label="Priority" value={<PriorityBadge priority={lead.priority} />} />
              <FIELD_ROW label="Customer Type" value={lead.customer_type} />
              <FIELD_ROW label="Source" value={lead.source?.replace('_', ' ')} />
              <FIELD_ROW label="Region" value={lead.region} />
              <FIELD_ROW label="Assigned To" value={lead.assigned_to?.name} />
              <FIELD_ROW label="Deal Value" value={lead.deal_value ? `₹${Number(lead.deal_value).toLocaleString('en-IN')}` : null} />
              {lead.win_loss_reason && <FIELD_ROW label="Win/Loss Reason" value={lead.win_loss_reason} />}
              <FIELD_ROW label="Stale?" value={lead.is_stale ? <span style={{ color: '#d97706', fontWeight: 600 }}>⚠️ Yes (no activity 7d+)</span> : 'No'} />
              <FIELD_ROW label="Scholarship" value={lead.scholarship_flag ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✅ Eligible</span> : 'Not applicable'} />
              {lead.notes && <FIELD_ROW label="Notes" value={lead.notes} />}
            </>
          )}
        </Card>

        {/* Stage History */}
        {lead.stage_history?.length > 0 && (
          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>Stage History</h3>
            {lead.stage_history.slice().reverse().map((h, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <StageBadge stage={h.stage} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {h.note && <p style={{ fontSize: 12, color: '#475569' }}>{h.note}</p>}
                  <p style={{ fontSize: 11, color: '#94a3b8' }}>by {h.changed_by?.name || '—'} · {new Date(h.timestamp).toLocaleString('en-IN')}</p>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* Right column */}
      <div>
        {/* Contact */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B' }}>Contact Information</h3>
          </div>
          {editing ? (
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>NAME</label>
                <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} style={inpStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>PHONE</label>
                <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} style={inpStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>EMAIL</label>
                <input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} style={inpStyle} />
              </div>
            </div>
          ) : (
            <>
              <FIELD_ROW label="Name" value={lead.contact_name} />
              <FIELD_ROW label="Phone" value={lead.contact_phone ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <a href={`tel:${lead.contact_phone}`} style={{ color: '#1B2A6B', textDecoration: 'none', fontWeight: 600 }}>{lead.contact_phone}</a>
                  <button onClick={() => setWaModal(true)} title="Send WhatsApp"
                    style={{ background: '#25d366', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 13, padding: '2px 8px', lineHeight: 1.4 }}>
                    💬 WA
                  </button>
                </span>
              ) : null} />
              <FIELD_ROW label="Email" value={lead.contact_email ? <a href={`mailto:${lead.contact_email}`} style={{ color: '#1B2A6B', textDecoration: 'none' }}>{lead.contact_email}</a> : null} />
            </>
          )}
        </Card>

        {/* Collaborators */}
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B' }}>Collaborators</h3>
            {isAdminOrMD && !collabAdding && (
              <button
                onClick={() => setCollabAdding(true)}
                disabled={collabSaving || availableToAdd.length === 0}
                style={{ fontSize: 12, fontWeight: 600, color: '#1B2A6B', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', cursor: availableToAdd.length === 0 ? 'not-allowed' : 'pointer', opacity: availableToAdd.length === 0 ? 0.4 : 1 }}
              >
                + Add
              </button>
            )}
          </div>

          {/* Add collaborator row */}
          {isAdminOrMD && collabAdding && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <select
                value={collabSelected}
                onChange={e => setCollabSelected(e.target.value)}
                style={{ ...selStyle, flex: 1 }}
                autoFocus
              >
                <option value="">Select person…</option>
                {availableToAdd.map(m => (
                  <option key={m._id} value={m._id}>{m.name} ({m.sub_role || m.role})</option>
                ))}
              </select>
              <button
                onClick={addCollaborator}
                disabled={!collabSelected || collabSaving}
                style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: '#1B2A6B', border: 'none', borderRadius: 6, padding: '0 14px', cursor: 'pointer', opacity: !collabSelected || collabSaving ? 0.6 : 1 }}
              >
                {collabSaving ? '…' : 'Add'}
              </button>
              <button
                onClick={() => { setCollabAdding(false); setCollabSelected(''); }}
                style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '0 10px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Collaborators list */}
          {collaborators.length === 0 ? (
            <p style={{ fontSize: 13, color: '#cbd5e1', margin: 0 }}>No collaborators yet.</p>
          ) : (
            collaborators.map(c => (
              <div key={c._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#1B2A6B', flexShrink: 0 }}>
                    {c.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#334155' }}>{c.name}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{c.sub_role || c.role}</p>
                  </div>
                </div>
                {isAdminOrMD && (
                  <button
                    onClick={() => removeCollaborator(c._id)}
                    disabled={collabSaving}
                    title="Remove collaborator"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1, padding: '2px 4px', borderRadius: 4 }}
                    onMouseEnter={e => e.target.style.color = '#ef4444'}
                    onMouseLeave={e => e.target.style.color = '#94a3b8'}
                  >
                    ×
                  </button>
                )}
              </div>
            ))
          )}
        </Card>

        {/* Type-Specific Data — always shown when editing (even if empty), shown in view when data exists */}
        {(editing || Object.keys(tsd).length > 0) && (
          <Card style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>{lead.customer_type} Details</h3>
            {editing ? (
              <TypeSpecificFields
                type={lead.customer_type}
                data={typeData}
                setData={setT}
              />
            ) : (
              <>
                {Object.entries(tsd).filter(([k]) => k !== 'departments').map(([k, v]) => (
                  <FIELD_ROW key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)} />
                ))}
                {tsd.departments?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>DEPARTMENTS</p>
                    {tsd.departments.map((d, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#334155', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                        {d.name} {d.hod && `· HOD: ${d.hod}`} {d.student_count && `· ${d.student_count} students`}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {/* Program Fee */}
        <Card>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>Program Fee</h3>
          <FIELD_ROW label="Base Fee" value={`₹${PROGRAM_FEE.base.toLocaleString('en-IN')}`} />
          <FIELD_ROW label="GST (18%)" value={`₹${PROGRAM_FEE.gst.toLocaleString('en-IN')}`} />
          <FIELD_ROW label="Total" value={<span style={{ fontWeight: 700, color: '#1B2A6B' }}>{`₹${PROGRAM_FEE.total.toLocaleString('en-IN')}`}</span>} />
          <FIELD_ROW label="Franchisee Commission (10%)" value={`₹${PROGRAM_FEE.commission.toLocaleString('en-IN')} / enrollment`} />
        </Card>
      </div>
    </div>
    {waModal && (
      <WhatsAppModal
        targetId={lead._id}
        targetType="lead"
        phone={lead.contact_phone}
        recipientName={lead.contact_name}
        onClose={() => setWaModal(false)}
      />
    )}
    </>
  );
}
